import React, { useState, useEffect } from 'react';
import Header from 'src/components/demo-day/header/Header';
import { Button } from 'src/components/common';
import { Modal } from 'react-bootstrap';
import { InputField } from 'src/components/common/formFields';
import { CompanyAPIService } from 'src/services/company.service';
import { openPopup } from 'src/store/common/common.slice';
import { useDispatch } from 'react-redux';
import { useGetIdentity } from 'src/hooks';
import { useNavigate } from 'react-router-dom';
import { TGetCompany, TGetCompanyResponse } from 'src/services/apiEndpoint.types.ts';
import 'bootstrap/dist/css/bootstrap.min.css';
import styles from './scoreLanding.module.scss';
import RocketIcon from 'src/assets/images/banner/RocketIcon.svg';

const ScoreLanding = (): React.ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getIdentities } = useGetIdentity();
  const { authenticatedId, loggedInUserType, authenticatedEmail } = getIdentities();
  const companyServiceRef = React.useRef<CompanyAPIService | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingCompany, setCheckingCompany] = useState(true);
  const [comp, setComp] = useState<TGetCompany | null>(null);

  const [formData, setFormData] = useState<{
    startupName: string;
    whatsappNumber: string;
    companyEmailAddress: string;
    termsAccepted: boolean;
  }>({
    startupName: comp?.name || '',
    whatsappNumber: comp?.contactNumber?.toString() || '',
    companyEmailAddress: comp?.companyEmailAddress || authenticatedEmail || '',
    termsAccepted: comp?.termsAndConditions || false
  });

  // Initialize companyService once
  useEffect(() => {
    companyServiceRef.current = new CompanyAPIService();
  }, []);

  // Initialize checking state and fetch company data
  useEffect(() => {
    let mounted = true;

    const fetchCompanyData = async () => {
      if (!authenticatedId || !companyServiceRef.current) return;

      try {
        const response = await companyServiceRef.current.getCompany<TGetCompanyResponse>({
          id: authenticatedId
        });

        if (mounted && response.status && response.data?.company) {
          setComp(response.data.company);
        }
      } catch (error) {
        if (mounted) {
          console.error('Error fetching company:', error);
        }
      } finally {
        if (mounted) {
          setCheckingCompany(false);
        }
      }
    };

    fetchCompanyData();

    return () => {
      mounted = false;
    };
  }, [authenticatedId]);

  const handleOpenModal = async () => {
    try {
      setCheckingCompany(true);
      
      // Always fetch fresh company data when clicked
      const response = await companyServiceRef.current.getCompany<TGetCompanyResponse>({
        id: authenticatedId
      });

      if (response.status && response.data?.company) {
        setComp(response.data.company);
        // If terms are accepted, navigate to score page
        if (response.data.company.termsAndConditions) {
          return;
        }
      }
      
      // If we get here, either no company exists or terms not accepted
      setShowModal(true);
    } catch (error) {
      console.error('Error checking company:', error);
      setShowModal(true);
    } finally {
      setCheckingCompany(false);
    }
  };

  // Update form data when company data changes
  useEffect(() => {
    if (comp) {
      setFormData({
        startupName: comp.name || '',
        whatsappNumber: comp.contactNumber?.toString() || '',
        companyEmailAddress: comp.companyEmailAddress || authenticatedEmail || '',
        termsAccepted: comp.termsAndConditions || false
      });
    } else {
      setFormData(prev => ({
        ...prev,
        companyEmailAddress: authenticatedEmail || ''
      }));
    }
  }, [comp, authenticatedEmail]);

  const handleInputChange = (field: keyof typeof formData) => (value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.startupName || !formData.whatsappNumber || !formData.companyEmailAddress || !formData.termsAccepted) {
      dispatch(
        openPopup({
          popupMessage: 'Please fill all required fields and accept terms & conditions',
          popupType: 'error'
        })
      );
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.companyEmailAddress)) {
      dispatch(
        openPopup({
          popupMessage: 'Please enter a valid company email address',
          popupType: 'error'
        })
      );
      return;
    }

    try {
      setLoading(true);
      let response;

      if (comp?.id) {
        response = await companyServiceRef.current.editCompany({
          id: comp.id,
          name: formData.startupName || comp.name,
          type: 'Start up',
          contactNumber: parseInt(formData.whatsappNumber.replace(/\D/g, ''), 10),
          companyEmailAddress: formData.companyEmailAddress,
          termsAndConditions: formData.termsAccepted,
          about: comp.about || '',
          website: comp.website || '',
          introduction: comp.introduction || '',
          foundedYear: comp.foundedYear || 0,
          contactUsUrl: comp.contactUsUrl || '',
          addressLine1: comp.addressLine1 || '',
          addressLine2: comp.addressLine2 || '',
          country: comp.country || '',
          state: comp.state || '',
          city: comp.city || '',
          zipCode: comp.zipCode || '',
          industry: comp.industry || '',
          linkedin: comp.linkedin || '',
          facebook: comp.facebook || '',
          twitter: comp.twitter || '',
          instagram: comp.instagram || ''
        });
      } else {
        response = await companyServiceRef.current.createCompany({
          email: authenticatedEmail,
          name: formData.startupName,
          contactNumber: parseInt(formData.whatsappNumber.replace(/\D/g, ''), 10),
          type: 'Start up',
          companyEmailAddress: formData.companyEmailAddress,
          termsAndConditions: formData.termsAccepted,
          consentTimeStamp: new Date().toISOString(),
          navigateTo: '/score'
        });
      }

      if (response.status) {
        dispatch(
          openPopup({
            popupMessage: comp?.id ? 'Company updated successfully!' : 'Company created successfully!',
            popupType: 'success'
          })
        );
        setShowModal(false);
        if (formData.termsAccepted) {
          navigate('/score');
        }
      } else {
        dispatch(
          openPopup({
            popupMessage: response.message || 'Failed to save company',
            popupType: 'error'
          })
        );
      }
    } catch (error) {
      dispatch(
        openPopup({
          popupMessage: 'An error occurred while creating company',
          popupType: 'error'
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.scoreLandingPage}>
      <Header />
      <div className={styles.heroSection}>
        <div className={styles.content}>
          <h1>Get Your Startup Scored</h1>
          <p className={styles.subtitle}>
            Connect with top investors by showcasing your startups potential through our comprehensive scoring system.
          </p>
          <div className={styles.scoreInfo}>
            <div className={styles.infoCard}>
              <h3>Why Get Scored?</h3>
              <ul>
                <li>Understand your startup&apos;s strengths and areas for improvement</li>
                <li>Get validated metrics that investors trust</li>
                <li>Increase your chances of securing investment</li>
                <li>Benchmark against successful startups</li>
              </ul>
            </div>
          </div>
          <div className={styles.ctaSection}>
            <Button
              variant="primary"
              size="large"
              onClick={handleOpenModal}
              disabled={checkingCompany}
            >
              {checkingCompany ? (
                <div className="spinner-border spinner-border-sm text-light" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              ) : (
                'Connect with Investors'
              )}
            </Button>
            <p className={styles.ctaNote}>
              Join hundreds of startups who have successfully connected with investors through our platform
            </p>
          </div>
        </div>
      </div>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        className={styles.setupModal}
      >
        <Modal.Body className={styles.setupModalBody}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <div>
                <img src={RocketIcon} alt="htraction" className={styles.rocketIcon} />
              </div>
              <h2 className={styles.modalTitle}>Setup Your Company Profile</h2>
            </div>
            <div className={styles.formContent}>
              <div className={styles.formField}>
                <InputField
                  id="startup-name"
                  label="Company Name"
                  value={formData.startupName}
                  onChange={(e) => handleInputChange('startupName')(e.target.value)}
                  placeholder="xyz.pvt.ltd"
                  required
                />
              </div>
              <div className={styles.formField}>
                <InputField
                  id="company-email"
                  label="Company Email"
                  value={formData.companyEmailAddress}
                  onChange={(e) => handleInputChange('companyEmailAddress')(e.target.value)}
                  placeholder="abc@htraction.com"
                  type="email"
                  required
                />
              </div>
              <div className={styles.formField}>
                <InputField
                  id="whatsapp-number"
                  label="Whatsapp Number"
                  value={formData.whatsappNumber}
                  onChange={(e) => handleInputChange('whatsappNumber')(e.target.value)}
                  placeholder="+91"
                  type="tel"
                  required
                />
              </div>
              <div className={styles.termsContainer}>
                <input
                  type="checkbox"
                  id="terms"
                  checked={formData.termsAccepted}
                  onChange={(e) => handleInputChange('termsAccepted')(e.target.checked)}
                />
                <label htmlFor="terms" className={styles.termsText}>
                  I accept the <a href="/privacy-policy" className={styles.termsLink}>terms & conditions</a>
                </label>
              </div>
              <Button 
                variant="primary" 
                onClick={handleSubmit} 
                disabled={loading}
                className={styles.submitButton}
              >
                {loading ? (
                  <div className="spinner-border spinner-border-sm text-light" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                ) : (
                  'Save & Continue'
                )}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ScoreLanding;
